import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { VITE_WORKSPACE_AGENT_ENABLED } from 'src/constants/envs';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import {
  WorkspaceAgentContext,
  type tWorkspaceAgentContext,
} from './Workspace.context';
import type { tWorkspaceRuntime } from './Workspace.runtime';
import { useWorkspaceAgentRollout } from './Workspace.rollout.hook';
import type { tConfigDraftSlot } from 'src/pages/LeadAgentConfig/data/source';

export default function WorkspaceAgentProvider(props: { children: ReactNode }) {
  const rolloutEnabled = useWorkspaceAgentRollout();
  const { currentWorkspaceId } = useWorkspaceContext(),
    { currentUserId, currentUserRole } = useUserContext(),
    { workspaceUpdatedEvent } = useSocketsContext();
  const enabled =
    VITE_WORKSPACE_AGENT_ENABLED &&
    rolloutEnabled &&
    !!currentWorkspaceId &&
    !!currentUserId &&
    ['OWNER', 'ADMIN', 'SELLER'].includes(currentUserRole);
  const scope = enabled
    ? `${currentWorkspaceId}:${currentUserId}:${currentUserRole}`
    : null;
  const draft = useMemo<tConfigDraftSlot | undefined>(
    () =>
      enabled && currentUserRole !== 'SELLER' ? { current: null } : undefined,
    [scope, enabled, currentUserRole],
  );
  useEffect(
    () => () => {
      if (draft) draft.current = null;
    },
    [draft],
  );
  const [revision, setRevision] = useState(0),
    [resolved, setResolved] = useState<{
      key: symbol;
      value: tWorkspaceAgentContext;
    }>();
  const key = useMemo(() => Symbol(scope ?? 'disabled'), [scope, revision]),
    retry = useCallback(() => setRevision((value) => value + 1), []);
  workspaceUpdatedEvent.useSubscription(() => {
    if (enabled) retry();
  });
  useEffect(() => {
    if (!enabled || !currentWorkspaceId || !scope) return;
    let closed = false,
      runtime: tWorkspaceRuntime | undefined;
    void import('./Workspace.runtime')
      .then(async (module) => {
        if (closed) return;
        runtime = module.createWorkspaceRuntime({
          workspaceId: currentWorkspaceId,
          scope,
        });
        const access = await runtime.client.access();
        if (!closed)
          setResolved({
            key,
            value: { ...runtime, status: 'ready', access, retry },
          });
      })
      .catch(() => {
        runtime?.controller.dispose();
        if (!closed)
          setResolved({ key, value: { status: 'unavailable', scope, retry } });
      });
    return () => {
      closed = true;
      runtime?.controller.dispose();
    };
  }, [enabled, currentWorkspaceId, scope, key, retry]);
  const value = useMemo<tWorkspaceAgentContext>(() => {
    if (!enabled) return { status: 'disabled', scope: null, retry };
    if (resolved?.key === key) return { ...resolved.value, draft };
    return { status: 'loading', scope, retry, draft };
  }, [enabled, key, resolved, retry, scope, draft]);
  return (
    <WorkspaceAgentContext.Provider value={value}>
      {props.children}
    </WorkspaceAgentContext.Provider>
  );
}
